import * as React from "react";
import { useNavigate } from "@opendash/router";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import {
  TabContainer,
  TabButton,
  ContentContainer,
  Card,
  InfoBox,
  ImageContainer,
  FigureCaption,
  SourcesSection,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";
import styled from "styled-components";

// Asset Imports
const aralseeImg = require("../../assets/wafuss1.jpg");
const aralseeVergleichImg = require("../../assets/wafuss2.png");
const gelberFlussImg = require("../../assets/wafuss3.jpg");
const wasserfussabdruckImg = require("../../assets/wafuss4.png");
const konsumVerbrauchImg = require("../../assets/wafuss5.png");

// Custom Styled Components (spezifisch für diese Route)
const HighlightBox = styled.div`
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-left: 4px solid #4caf50;
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;

  &::before {
    content: "FAZIT";
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #2e7d32;
    opacity: 0.6;
    margin-bottom: 8px;
  }

  h4 {
    color: #2e7d32;
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 8px;
  }
`;

/**
 * WasserknappheitFussabdruckRoute – Wasserfußabdruck
 */
export const WasserknappheitFussabdruckRoute = React.memo(
  function WasserknappheitFussabdruckRoute() {
    const navigate = useNavigate();
    const { t } = useTexts();

    const tabs = [
      {
        title: t("wasserknappheit.tab.grundlagen", "Grundlagen"),
        path: "/wasserklaerer/wasserknappheit/grundlagen",
        isActive: false,
      },
      {
        title: t("wasserknappheit.tab.entstehung", "Entstehung"),
        path: "/wasserklaerer/wasserknappheit/entstehung",
        isActive: false,
      },
      {
        title: t("wasserknappheit.tab.fussabdruck", "Wasserfußabdruck"),
        path: "/wasserklaerer/wasserknappheit/fussabdruck",
        isActive: true,
      },
    ];

    return (
      <AppLayout>
        <ContentContainer>
          <h1>{t("wasserknappheit.title", "Wasserknappheit")}</h1>

          <TabContainer>
            {tabs.map((tab, index) => (
              <TabButton
                key={index}
                isActive={tab.isActive}
                onClick={() => !tab.isActive && navigate(tab.path)}
              >
                {tab.title}
              </TabButton>
            ))}
          </TabContainer>

          {/* Einleitung */}
          <Card>
            <h2>
              {t("wasserknappheit.fussabdruck.title", "Wasserfußabdruck")}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.aralsee.intro",
                  "Deutschland steht derzeit nicht unter akutem Wasserstress – doch wie sieht es jenseits unserer Grenzen aus? In vielen Regionen der Welt hat sich die Wasserknappheit in den letzten Jahren dramatisch verschärft."
                )
              )}
            </p>
          </Card>

          {/* Aralsee */}
          <Card>
            <h2>
              {t(
                "wasserknappheit.fussabdruck.aralsee.title",
                "Salz statt Wasser – das Drama des Aralsees"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.aralsee.intro",
                  "Der Aralsee ist ein kollabiertes Ökosystem. Seine Austrocknung wird als eine der gravierendsten wasserbezogenen Umweltkatastrophen des 20. Jahrhunderts eingestuft. [1]"
                )
              )}
            </p>

            <ImageContainer>
              <img src={aralseeImg} alt="Aralsee nach Austrocknung" />
              <FigureCaption>
                Abbildung 1:{" "}
                {t(
                  "wasserknappheit.fussabdruck.aralsee.caption1",
                  "Aralsee nach Austrocknung, Zhanat Kulenov, CC BY-SA 3.0 IGO, via Wikimedia Commons"
                )}
              </FigureCaption>
            </ImageContainer>

            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.aralsee.text1",
                  "Der Aralsee war einst das viertgrößte Binnengewässer der Erde. Seine Uferstädte florierten und lebten vom Handel mit Bisampelzen und von einer reichen Fischerei, die rund ein Sechstel des sowjetischen Fischbedarfs deckte. Gespeist wurde der See von zwei mächtigen Strömen: dem Amudarja im Süden und dem Syrdarja im Norden. [2]"
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.aralsee.text2",
                  "In den 1960er-Jahren änderte sich alles. Die Sowjetunion ließ riesige Bewässerungssysteme errichten, um Baumwolle und Weizen in den Steppen Kasachstans und Usbekistans anzubauen. Doch die Kanäle waren undicht und ineffizient. Immer größere Wassermengen versickerten oder verdunsteten, bis schließlich der Amudarja den Aralsee überhaupt nicht mehr erreichte – heute versiegt er rund 110 Kilometer vor dessen ehemaliger Küste. [2]"
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.aralsee.text3",
                  "Die Folgen waren dramatisch: Der See schrumpfte in wenigen Jahrzehnten zu einer Gruppe kleiner Restseen zusammen, deren Gesamtvolumen nur noch ein Zehntel der ursprünglichen Wassermenge beträgt. Mit dem Wasser verschwand das Leben – Millionen Fische starben, Pflanzenarten verschwanden, und das Ufer zog sich kilometerweit von den Städten zurück. [2] [3]"
                )
              )}
            </p>

            <ImageContainer>
              <img
                src={aralseeVergleichImg}
                alt="Größe des Aralsees früher und heute"
              />
              <FigureCaption>
                Abbildung 2:{" "}
                {t(
                  "wasserknappheit.fussabdruck.aralsee.caption2",
                  "Größe des Aralsees früher und heute (eigene Darstellung) [4]"
                )}
              </FigureCaption>
            </ImageContainer>

            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.aralsee.text4",
                  "Aus dem einstigen Seegrund entstand eine Salzwüste. Starke Verdunstung hinterließ eine Kruste aus Salz, die vom Wind verweht wird. Staubstürme tragen nicht nur Salz, sondern auch Pestizide aus der Landwirtschaft und Rückstände von sowjetischen Waffentests über weite Teile der Region. Zahlreiche Menschen verloren ihre Lebensgrundlage, und die, die blieben, kämpfen bis heute mit gesundheitlichen Belastungen und einer zerstörten Umwelt. [2] [3]"
                )
              )}
            </p>
          </Card>

          {/* Gelber Fluss */}
          <Card>
            <h2>
              {t(
                "wasserknappheit.fussabdruck.yellowriver.title",
                "Gelber Fluss - Lebensader und Leidensweg"
              )}
            </h2>

            <ImageContainer>
              <img src={gelberFlussImg} alt="Gelber Fluss China" />
              <FigureCaption>
                Abbildung 3:{" "}
                {t(
                  "wasserknappheit.fussabdruck.yellowriver.caption",
                  "Gelber Fluss China, fading, CC BY-SA 3.0, via Wikimedia Commons"
                )}
              </FigureCaption>
            </ImageContainer>

            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.yellowriver.text1",
                  "Mit 5.464 Kilometern ist der Gelbe Fluss nach dem Jangtse der zweitlängste Strom Chinas und der sechstgrößte der Welt. Seine Ufer gelten als Wiege der chinesischen Zivilisation. Doch der Fluss ist auch für seine zerstörerische Kraft berüchtigt: Immer wieder führten Überschwemmungen zu Katastrophen, die vielen Menschen das Leben kosteten. Seit den letzten Jahrzehnten steht der Gelbe Fluss jedoch vor einem gegenteiligen Problem: Er versiegt regelmäßig, bevor er das Meer erreicht. Vor allem die extensive landwirtschaftliche und industrielle Nutzung entzieht ihm enorme Wassermengen. Hinzu kommen zahlreiche Staudämme und eine wachsende Bevölkerung und Urbanisierung, die das Problem verschlimmern. 1997 führte er an 230 Tagen überhaupt kein Wasser. Die Folgen sind gravierend – sein Delta trocknete aus, die ökologische Vielfalt ging verloren, und Wind erodierte die feinen Lössböden. In jüngerer Zeit versucht die chinesische Regierung gegenzusteuern. So wurden entlang des Flusses Bauern von der Nutzung des Flusswassers ausgeschlossen, um den Strom wieder dauerhaft zum Meer fließen zu lassen. [2] [5]"
                )
              )}
            </p>
          </Card>

          {/* Drei Farben des Wassers */}
          <Card>
            <h2>
              {t(
                "wasserknappheit.fussabdruck.colors.title",
                "Blau, Grün und Grau – drei Gesichter des Wassers"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.colors.intro",
                  "Um den Wasserverbrauch besser zu verstehen, wird zwischen blauem, grünem und grauem Wasser unterschieden: [7]"
                )
              )}
            </p>

            <ul>
              <li>
                {t(
                  "wasserknappheit.fussabdruck.colors.green",
                  "Grünes Wasser ist Regen- und Bodenwasser, das von Pflanzen aufgenommen und verdunstet wird – vor allem relevant für die Landwirtschaft."
                )}
              </li>
              <li>
                {t(
                  "wasserknappheit.fussabdruck.colors.blue",
                  "Blaues Wasser stammt aus Grund- oder Oberflächengewässern und wird z. B. für künstliche Bewässerung genutzt. Es gilt als besonders kritisch, da es nicht wieder in den Wasserkreislauf zurückgeführt wird."
                )}
              </li>
              <li>
                {t(
                  "wasserknappheit.fussabdruck.colors.grey",
                  "Graues Wasser beschreibt die Wassermenge, die nötig wäre, um Verschmutzungen so weit zu verdünnen, dass die gesetzlichen Qualitätsstandards wieder eingehalten werden."
                )}
              </li>
            </ul>

            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.colors.import",
                  'Gerade beim blauen Wasser fällt auf: Deutschland "importiert" den größten Teil seines Wasserverbrauchs. Die wichtigsten Herkunftsregionen sind Asien und der Pazifikraum (5,4 Mrd. m³), der Mittlere Osten (3,8 Mrd. m³), China (2,2 Mrd. m³) und Spanien (2,1 Mrd. m³). Auch Länder wie Indien, die USA, Afrika und die Türkei tragen nennenswerte Anteile bei. [6]'
                )
              )}
            </p>
          </Card>

          {/* Wasserfußabdruck-Konzept */}
          <Card>
            <h2>
              {t(
                "wasserknappheit.fussabdruck.concept.title",
                "Der Wasserfußabdruck - Wie die weltweiten Wasserprobleme auch uns betreffen"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.concept.text1",
                  "Deutschland gehört zu den fünf größten Konsummärkten weltweit. [6] Diese enorme Konsumnachfrage geht mit der weltweiten Produktion vieler Güter einher. Doch was man oft vergisst: Fast jedes dieser Güter benötigt Wasser. Ob Kaffee, Gemüse, Fleisch, Kleidung oder Elektronik – Wasser ist fast in jedem Produkt enthalten oder wurde zu dessen Erzeugung eingesetzt."
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.concept.text2",
                  'Diese Dimension des Wasserverbrauchs wird durch den Begriff des "Wasserfußabdrucks" erfasst, der den gesamten Wasserverbrauch eines Produkts, einer Person, eines Unternehmens oder einer Nation umfassen kann. Dabei geht es nicht nur um das Wasser, das direkt aus dem Wasserhahn bezogen wird, sondern um das gesamte Wasser, das für die Herstellung eines Produkts notwendig ist – von der Landwirtschaft über die Industrie bis hin zur Verarbeitung und dem Transport. Wir nennen diesen versteckten Teil "virtuelles Wasser", welcher sich als deinen indirekten Wasserverbrauch manifestiert. [7] Der mit Abstand größte Anteil an Deutschlands Wasserfußabdruck entspringt dem Ausland, da die Produktion vieler Güter nicht in Deutschland selbst stattfindet. Das wird zum Problem, wenn die Wasserbeanspruchung dann in Regionen stattfindet, die mit Wasserknappheit zu kämpfen haben. [6]'
                )
              )}
            </p>

            <ImageContainer>
              <img
                src={wasserfussabdruckImg}
                alt="Illustrative Darstellung der Zusammensetzung des Wasserfußabdrucks"
              />
              <FigureCaption>
                Abbildung 4:{" "}
                {t(
                  "wasserknappheit.fussabdruck.concept.caption",
                  "Illustrative Darstellung der Zusammensetzung des Wasserfußabdrucks (eigene Darstellung)"
                )}
              </FigureCaption>
            </ImageContainer>

            <h3>
              {t("wasserknappheit.fussabdruck.definition.title", "Definition")}
            </h3>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.definition.text",
                  "Wie wird der Wasserfußabdruck eigentlich genau definiert? Ursprünglich wurde er rein volumetrisch, also mengenmäßig, betrachtet. Dieses klassische Konzept wird als Wasserfußabdruck-Sachbilanz bezeichnet. Sie gibt an, wie viel Wasser für die Herstellung und Nutzung von Produkten und Dienstleistungen verbraucht wird – unabhängig von seiner potenziellen Umweltwirkung. [6]"
                )
              )}
            </p>

            <h3>
              {t(
                "wasserknappheit.fussabdruck.germanynumbers.title",
                "Deutschlands Wasserbilanz in Zahlen"
              )}
            </h3>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.germanynumbers.text1",
                  'Die Wasserfußabdruck-Sachbilanz Deutschlands summiert sich auf rund 219 Milliarden Kubikmeter Wasser pro Jahr. Umgerechnet entspricht das einem täglichen Pro-Kopf-Verbrauch von etwa 7200 L Wasser – also genug, um 36 Badewannen à 200 Liter pro Tag zu füllen. [6] Nicht zu verwechseln ist dein direkter Wasserverbrauch durch Trinken, Waschen und Kochen. Dieser liegt bei 126 L pro Tag. [8] Der restliche Anteil deines Wasserfußabdrucks ergibt sich also durch deinen Konsum. Folgende Abbildung zeigt, wie sich diese Zahl auf die verschiedenen "Farben des Wassers" und auf das In- und Ausland verteilt:'
                )
              )}
            </p>

            <ImageContainer>
              <img
                src={konsumVerbrauchImg}
                alt="Der konsuminduzierte Wasserverbrauch von Deutschland"
              />
              <FigureCaption>
                Abbildung 5:{" "}
                {t(
                  "wasserknappheit.fussabdruck.germanynumbers.caption",
                  "Der konsuminduzierte Wasserverbrauch von Deutschland im Inland und im Ausland [eigene Darstellung aus [6], Zahlen in Milliarden Kubikmeter Wasser pro Jahr]"
                )}
              </FigureCaption>
            </ImageContainer>
          </Card>

          {/* Sektoren */}
          <Card>
            <h2>
              {t(
                "wasserknappheit.fussabdruck.sectors.title",
                "Wohin das Wasser fließt: die wichtigsten Sektoren"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.sectors.intro",
                  "Ein Großteil des blauen Wassers entfällt auf landwirtschaftliche Produkte. Die größten Beiträge leisten die Sektoren: [6]"
                )
              )}
            </p>

            <ul>
              <li>
                {t(
                  "wasserknappheit.fussabdruck.sectors.vegetables",
                  "Gemüse, Früchte und Nüsse (6 Mrd. m³)"
                )}
              </li>
              <li>
                {t(
                  "wasserknappheit.fussabdruck.sectors.crops",
                  "Nutzpflanzen unspezifisch (4,9 Mrd. m³)"
                )}
              </li>
              <li>
                {t(
                  "wasserknappheit.fussabdruck.sectors.wheat",
                  "Weizen (2 Mrd. m³)"
                )}
              </li>
              <li>
                {t(
                  "wasserknappheit.fussabdruck.sectors.rice",
                  "Reis (1,7 Mrd. m³)"
                )}
              </li>
              <li>
                {t(
                  "wasserknappheit.fussabdruck.sectors.other",
                  "sowie Zuckerrohr, Ölsaaten und Viehzucht."
                )}
              </li>
            </ul>

            <p>
              {t(
                "wasserknappheit.fussabdruck.sectors.industry",
                "Industriesektoren wie Kunststoffe und Eisen/Stahl spielen im Vergleich dazu eine kleinere Rolle."
              )}
            </p>
          </Card>

          {/* ISO-Standard */}
          <Card>
            <h2>
              {t(
                "wasserknappheit.fussabdruck.iso.title",
                "Vom Volumen zur Wirkung – die neue Definition"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.iso.text1",
                  'Da die reine Mengenbetrachtung nur begrenzt aussagekräftig ist, wurde das Konzept weiterentwickelt. Der ISO-Standard 14046 (2014) definiert den Wasserfußabdruck als "Kennzahl(en) zur quantitativen Bestimmung der potenziellen Umweltwirkungen im Zusammenhang mit Wasser". [6]'
                )
              )}
            </p>
            <p>
              {t(
                "wasserknappheit.fussabdruck.iso.text2",
                "Das bedeutet: Nicht jede verbrauchte Wassermenge hat die gleiche ökologische Bedeutung. In wasserreichen Regionen fällt derselbe Verbrauch weniger ins Gewicht als in Gebieten mit chronischer Wasserknappheit. Der ISO-Standard berücksichtigt daher regionale Unterschiede und die tatsächlichen Umweltwirkungen des Wasserverbrauchs."
              )}
            </p>
            <p>
              {t(
                "wasserknappheit.fussabdruck.iso.text3",
                "Ein vollständiger Wasserfußabdruck betrachtet nicht nur den Verbrauch, sondern auch die Verschmutzung von Wasserressourcen. Dazu wird ein Wasserqualitätsindex vorgeschlagen, der die Wirkungskategorien Gewässereutrophierung, Ökotoxizität (in Gewässern und an Land) sowie Versauerung von Gewässern einschließt."
              )}
            </p>
            <p>
              {t(
                "wasserknappheit.fussabdruck.iso.text4",
                'Wenn eine Analyse nur Teilaspekte abbildet, sollte dies transparent gemacht werden – etwa als "nicht umfassender Wasserfußabdruck in Bezug auf Wasserknappheit".'
              )}
            </p>

            <h3>
              {t(
                "wasserknappheit.fussabdruck.truefootprint.title",
                'Deutschlands "wahrer Fußabdruck"'
              )}
            </h3>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.truefootprint.text1",
                  'Nun wurde Deutschlands "wahrer" Wasserfußabdruck mit Einbeziehung der Übernutzung der weltweiten Wasserressourcen angenähert. Dabei zeigte sich: Regionen wie der Mittlere Osten, Spanien, Afrika, Mexiko und Griechenland gewinnen durch ihre hohe Wasserknappheit deutlich an Bedeutung. [6]'
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.truefootprint.text2",
                  "Im Gegensatz dazu ist Deutschlands eigener Beitrag zur globalen Wasserübernutzung nahezu vernachlässigbar. Allerdings wird die Wasserverschmutzung in dieser Modellierung bislang noch nicht vollständig berücksichtigt – hier besteht weiterhin Forschungsbedarf. [6]"
                )
              )}
            </p>
          </Card>

          {/* Fazit */}
          <HighlightBox>
            <p>
              {t(
                "wasserknappheit.fussabdruck.fazit.text1",
                'Der Wasserfußabdruck zeigt eindrucksvoll, dass unser Konsumverhalten weit über nationale Grenzen hinauswirkt. Während wir in Deutschland scheinbar noch genügend Wasser haben, stammt ein erheblicher Teil unseres "virtuellen Wasserverbrauchs" aus dem Ausland. Hierbei kann die gleiche Wassermenge unterschiedliche Umweltauswirkungen haben, je nachdem aus welchen Regionen das Wasser bezogen wurde. Die Produktion landwirtschaftlicher und industrieller Güter kann Gewässer verschmutzen und so die Umwelt und die Gesundheit der dort lebenden Menschen schädigen. Beispiele wie die Tragödie des Aralsees zeigen, dass Wasserknappheit Regionen für Menschen unbewohnbar machen können. Migration kann in Zukunft immer präsenter werden da Menschen ihre Heimat verlassen müssen, weil die Ressourcen ausgeschöpft sind. All diese Auswirkungen kann man der gekauften Ware nicht ansehen.'
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.fussabdruck.fazit.text2",
                  "Es geht also nicht in erster Linie darum, den allgemeinen Wasserverbrauch pauschal zu senken. Wichtiger ist es, das Bewusstsein dafür zu schärfen, welche Güter wie viel Wasser benötigen und aus welchen Regionen dieses Wasser stammt. Neue Konzepte könnten von der Regierung eingeführt werden wie verpflichtende Siegel mit Angabe des Wasserfußabdrucks auf verschiedene Produkte, um Bürgern ein bewusstes Konsumverhalten zu ermöglichen und die Transparenz in Bezug auf die globale Wassernutzung zu steigern. Ziel muss sein, dass weder ökologische Schäden entstehen noch soziale Ungleichheiten durch Wasserknappheit verstärkt werden. [7]"
                )
              )}
            </p>
          </HighlightBox>

          <SourcesSection>
            <h3>
              {t(
                "wasserknappheit.fussabdruck.literature.title",
                "Literaturverzeichnis"
              )}
            </h3>
            <ol>
              <li id="quelle-1">
                <LinkifyText>
                  {t(
                    "wasserknappheit.fussabdruck.literature.ref1",
                    '[1] Franziska Wolf, Dr. Klaus Jacob, "Veröffentlichung des 6. Globalen Umweltberichts (GEO-6) 2019: Analyse der Implikationen für Deutschland", Dessau-Roßlau: Umweltbundesamt, 2019. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/sites/default/files/medien/1410/publikationen/2019-06-17_texte_59-2019_geo-6.pdf'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-2">
                <LinkifyText>
                  {t(
                    "wasserknappheit.fussabdruck.literature.ref2",
                    '[2] National Geographic (Hrsg.), "Galerie | Diese 8 gewaltigen Flüsse könnten schon bald austrocknen", National Geographic, 2019. Zugegriffen: 21. Oktober 2025. [Online]. Verfügbar unter: https://nationalgeographic.de/bildergalerie/2019/10/galerie-diese-8-gewaltigen-fluesse-koennten-schon-bald-austrocknen/'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-3">
                <LinkifyText>
                  {t(
                    "wasserknappheit.fussabdruck.literature.ref3",
                    '[3] Ministerium für Umwelt, Klima und Energiewirtschaft Baden-Württemberg (Hrsg.), "Binnenseen ohne Wasser", Baden-Württemberg, 2022. Zugegriffen: 21. Oktober 2025. [Online]. Verfügbar unter: https://um.baden-wuerttemberg.de/de/presse-service/ausstellungen/unser-planet/umwelt'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-4">
                <LinkifyText>
                  {t(
                    "wasserknappheit.fussabdruck.literature.ref4",
                    '[4] Google Earth, "Google Earth Aralsee Historische Bilder". Zugegriffen: 21. Oktober 2025. [Online]. Verfügbar unter: https://earth.google.com/web/search/aralsee/@46.07362019,60.84598182,293.16629118a,1008673.8395302d,35y,-0h,0t,0r/data=Cn0aRxJBCiQweDQxZWQyM2I0OWJmNjEwMGY6MHgzY2RlOTU0Mjc5ZThkNjkZcdkyKrqyRkAhmD_wQoTOTUAqB2FyYWxzZWUYAiABIiYKJAkbodbImMJHQBEOZlwEnr9FQBk_R8ICGZJQQCH1TOhDaCVLQCoGCAESABgBQgIIAToDCgEwQgIIAEoNCP___________wEQAA'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-5">
                <LinkifyText>
                  {t(
                    "wasserknappheit.fussabdruck.literature.ref5",
                    '[5] Nancy Allmrodt, "Infoblatt Der Gelbe Fluss", Klett, 2012. Zugegriffen: 22. Oktober 2025. [Online]. Verfügbar unter: https://www2.klett.de/sixcms/list.php?page=infothek_artikel&extra=&artikel_id=329718&inhalt=klett71prod_1.c.264543.de'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-6">
                <LinkifyText>
                  {t(
                    "wasserknappheit.fussabdruck.literature.ref6",
                    '[6] Technische Universität Berlin (Hrsg.), "Der Wasser-Fußabdruck von Deutschland", Water Footprint Tools, 2021. Zugegriffen: 22. Oktober 2025. [Online]. Verfügbar unter: https://wf-tools.see.tu-berlin.de/der-wasserfussabdruck-von-deutschland/'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-7">
                <LinkifyText>
                  {t(
                    "wasserknappheit.fussabdruck.literature.ref7",
                    '[7] Umweltbundesamt (Hrsg.), "Wasserfußabdruck", Umweltbundesamt, 2022. Zugegriffen: 27. Oktober 2025. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/themen/wasser/wasser-bewirtschaften/wasserfussabdruck'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-8">
                <LinkifyText>
                  {t(
                    "wasserknappheit.fussabdruck.literature.ref8",
                    '[8] Umweltbundesamt (Hrsg.), "Wassernutzung privater Haushalte", Umweltbundesamt, 2024. Zugegriffen: 27. Oktober 2025. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/daten/private-haushalte-konsum/wohnen/wassernutzung-privater-haushalte'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
            </ol>
          </SourcesSection>
        </ContentContainer>
      </AppLayout>
    );
  }
);
